/* AdminRiskEngine — global AI risk monitor.

   Shows the risk-engine health card, configuration toggles, top deteriorating
   buyers (across all sellers), recent alerts feed, and a manual "Run scan"
   button. Lives in the admin portal — only admins can see it. */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, RefreshCw, Sparkles, ShieldAlert, TrendingDown,
  Bell, CheckCircle2, Settings2, Database,
} from "lucide-react";

const ALERT_META = {
  score_drop:              { icon: TrendingDown, tone: "amber",   label: "Score drop" },
  score_drop_critical:     { icon: ShieldAlert,  tone: "rose",    label: "Critical drop" },
  new_overdue:             { icon: AlertTriangle, tone: "amber",  label: "New overdue" },
  cover_recommend_reduce:  { icon: Sparkles,     tone: "blue",    label: "Reduce cover" },
};

const tonePill = (tone) => ({
  rose:  "bg-rose-50  text-rose-800  border-rose-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  blue:  "bg-blue-50  text-blue-800  border-blue-200",
}[tone] || "bg-secondary text-foreground/70 border-border");

export default function AdminRiskEngine() {
  const [health, setHealth] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [top, setTop] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [cfg, setCfg] = useState(null);

  const load = async () => {
    const [h, a, t] = await Promise.all([
      api.get("/admin/risk-engine/health"),
      api.get("/admin/risk-engine/alerts?limit=50"),
      api.get("/admin/risk-engine/top-deteriorating"),
    ]);
    setHealth(h.data);
    setCfg(h.data.config);
    setAlerts(a.data || []);
    setTop(t.data || []);
  };

  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/risk-engine/scan");
      toast.success(`Scan done · ${data.inspected} buyers · ${data.alerts_emitted} alert(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await api.patch("/admin/risk-engine/config", cfg);
      toast.success("Config saved");
      await load();
    } catch {
      toast.error("Could not save config");
    } finally {
      setSavingCfg(false);
    }
  };

  if (!health) {
    return <div className="p-10 text-sm text-foreground/55">Loading risk engine…</div>;
  }

  return (
    <div data-testid="admin-risk-engine">
      <PageHeader
        title="AI Risk Engine"
        subtitle="Continuous global buyer-risk surveillance · runs daily · auto-notifies affected sellers"
        action={
          <Button onClick={runScan} disabled={scanning} data-testid="risk-run-scan" className="rounded-full bg-foreground text-background hover:bg-foreground/90">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${scanning ? "animate-spin" : ""}`}/>
            {scanning ? "Scanning…" : "Run scan now"}
          </Button>
        }
      />

      <div className="p-8 space-y-6">

        {/* Status banner */}
        <div className="rounded-2xl border border-border bg-white p-5 flex items-center gap-4" data-testid="risk-status-banner">
          <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${cfg?.enabled ? "bg-emerald-500 text-white" : "bg-foreground/30 text-white"}`}>
            <Activity className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">
              {cfg?.enabled ? "Risk engine · running" : "Risk engine · disabled"}
            </div>
            <div className="text-[11.5px] text-foreground/60 mt-0.5">
              {health.last_run
                ? <>Last run {new Date(health.last_run.finished_at).toLocaleString()} · inspected {health.last_run.inspected} buyer(s) · emitted {health.last_run.alerts_emitted} alert(s)</>
                : "Hasn't run yet — click 'Run scan now' to seed."}
            </div>
          </div>
          <div className="text-[10px] mono uppercase tracking-wider text-foreground/50 shrink-0">{cfg?.enabled ? "AUTO" : "PAUSED"}</div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="risk-kpis">
          <KPI label="Snapshots stored" value={health.snapshots} hint="Daily score history points"/>
          <KPI label="Alerts today"    value={health.alerts_today} hint="Triggered in last 24h"/>
          <KPI label="Alerts lifetime" value={health.alerts_lifetime} hint="All time"/>
          <KPI label="Drop threshold"  value={`${cfg?.score_drop_threshold || "—"} pts`} hint={`Critical at ${cfg?.score_drop_critical || "—"}`}/>
        </div>

        {/* Configuration */}
        <div className="rounded-2xl border border-border bg-white p-6" data-testid="risk-config">
          <div className="flex items-center gap-2 mb-4">
            <Settings2 className="w-4 h-4"/>
            <div className="display text-base">Configuration</div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Auto-scan enabled</div>
                <div className="text-[11.5px] text-foreground/55">Daily background scan that emits alerts.</div>
              </div>
              <Switch
                checked={!!cfg?.enabled}
                onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                data-testid="risk-enabled-switch"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-border">
              <FormRow
                label="Score-drop threshold (warning)"
                hint="Emit a yellow alert when a buyer's score drops by this many points."
              >
                <Input
                  type="number"
                  value={cfg?.score_drop_threshold ?? 30}
                  onChange={(e) => setCfg({ ...cfg, score_drop_threshold: Number(e.target.value) })}
                  data-testid="risk-cfg-threshold"
                  className="h-10 mono"
                />
              </FormRow>
              <FormRow
                label="Score-drop threshold (critical)"
                hint="Emit a red alert + recommend reducing insurance cover."
              >
                <Input
                  type="number"
                  value={cfg?.score_drop_critical ?? 60}
                  onChange={(e) => setCfg({ ...cfg, score_drop_critical: Number(e.target.value) })}
                  data-testid="risk-cfg-critical"
                  className="h-10 mono"
                />
              </FormRow>
              <FormRow
                label="Overdue $ threshold"
                hint="Trigger 'new overdue' alert when buyer crosses this outstanding."
              >
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 text-sm">$</span>
                  <Input
                    type="number"
                    value={cfg?.overdue_dollars_threshold ?? 5000}
                    onChange={(e) => setCfg({ ...cfg, overdue_dollars_threshold: Number(e.target.value) })}
                    data-testid="risk-cfg-overdue"
                    className="h-10 pl-7 mono"
                  />
                </div>
              </FormRow>
              <FormRow
                label="Cover-reduce haircut"
                hint="Suggested limit = current × this multiplier."
              >
                <Input
                  type="number"
                  step="0.05"
                  min="0.1"
                  max="0.95"
                  value={cfg?.limit_reduce_haircut ?? 0.6}
                  onChange={(e) => setCfg({ ...cfg, limit_reduce_haircut: Number(e.target.value) })}
                  data-testid="risk-cfg-haircut"
                  className="h-10 mono"
                />
              </FormRow>
            </div>

            <Button onClick={saveCfg} disabled={savingCfg} data-testid="risk-save-cfg" className="rounded-full bg-foreground text-background hover:bg-foreground/90">
              {savingCfg ? "Saving…" : "Save configuration"}
            </Button>
          </div>
        </div>

        {/* Top deteriorating + Alerts feed */}
        <div className="grid lg:grid-cols-2 gap-6">

          <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="risk-deteriorating">
            <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
              <TrendingDown className="w-4 h-4"/>
              <div className="text-sm font-medium">Top deteriorating buyers</div>
            </div>
            {top.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-foreground/55">
                No comparison data yet — needs at least 2 days of snapshots to show movement.
              </div>
            ) : (
              <div className="divide-y divide-border">
                {top.map((b) => (
                  <div key={b.buyer_id} className="px-5 py-3 flex items-center gap-3" data-testid={`top-row-${b.buyer_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{b.buyer_name || b.buyer_id.slice(0, 12)}</div>
                      <div className="text-[11px] text-foreground/55 mono">
                        {b.previous_score} → {b.latest_score}
                      </div>
                    </div>
                    <div className={`text-sm display ${b.delta < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                      {b.delta > 0 ? "+" : ""}{b.delta}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="risk-alerts">
            <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
              <Bell className="w-4 h-4"/>
              <div className="text-sm font-medium">Recent alerts</div>
              <span className="ml-auto text-[10px] mono uppercase tracking-wider text-foreground/55">{alerts.length}</span>
            </div>
            {alerts.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-foreground/55">
                No alerts emitted yet.
              </div>
            ) : (
              <div className="divide-y divide-border max-h-[480px] overflow-y-auto">
                {alerts.map((a) => {
                  const meta = ALERT_META[a.kind] || { icon: Activity, tone: "secondary", label: a.kind };
                  const Icon = meta.icon;
                  return (
                    <div key={a.id} className="px-5 py-3 flex items-start gap-3" data-testid={`alert-${a.id}`}>
                      <Icon className="w-4 h-4 mt-0.5 shrink-0 text-foreground/65"/>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border ${tonePill(meta.tone)}`}>
                            {meta.label}
                          </span>
                          <span className="text-sm font-medium truncate">{a.buyer_name}</span>
                        </div>
                        <div className="text-[11.5px] text-foreground/65 mt-0.5 line-clamp-2">{a.body}</div>
                        <div className="text-[10px] text-foreground/45 mono uppercase tracking-wider mt-1">
                          {new Date(a.created_at).toLocaleString()} · seller {(a.seller_id || "").slice(0, 8)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="text-[11px] text-foreground/45 inline-flex items-center gap-1.5">
          <Database className="w-3 h-3"/>
          Engine reads global score signals, payment history, and Allianz exposure. Sellers only see alerts for their connected buyers — buyers never see this data.
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4">
      <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{label}</div>
      <div className="display text-2xl mt-1.5 tracking-tight">{value}</div>
      {hint && <div className="text-[11px] text-foreground/55 mt-1 truncate">{hint}</div>}
    </div>
  );
}

function FormRow({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs mono uppercase tracking-[0.18em] text-foreground/50">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-[11px] text-foreground/50 mt-1">{hint}</div>}
    </div>
  );
}
